import { Button, DialogContent, DialogActions, CircularProgress, Box, Typography, Fade } from "@mui/material";
import axios from "axios";
import { useForm, UseFormReturn } from "react-hook-form";
import { useState, useEffect } from "react";
import { useRelatedRecords } from "../../../api/useRelatedRecords";
import { useStore } from "../../../store/store";
import { MAKE_WEBHOOKS } from "../../../utils/constants";
import { Wysiwyg } from "../../../components/wysiwyg/Wysiwyg";
import { generateTreatmentPrescriptionPdf } from "./generateTreatmentPrescriptionPdf";
import { ClientAndRelativesEmailSelectionField } from "../../../components/fieldsInputs/fields/customized/ClientAndRelativesEmailSelectionField";
import { PdfDocumentDownloadButton } from "../../../components/pdf/PdfDownloadButton";
import { useUpdateRecord } from "../../../api/useUpdateRecord";

type TreatmentPrescDocumentDialogProps = {
	context: any;
	onClose: () => void;
};

export const TreatmentPrescDocumentDialog = ({ context, onClose }: TreatmentPrescDocumentDialogProps) => {
	const { notifications } = useStore();
	const [isLoading, setIsLoading] = useState(false);
	const [downloadUrl, setDownloadUrl] = useState<string>("");
	const [pdfGenerationError, setPdfGenerationError] = useState(false);
	const { mutate: updateRecord } = useUpdateRecord( { queryInvalidatePrefix: "treatment_prescription" });

	const treatmentId = context?.id
	const client = context?.item?.client;
	const clientName = client?.user_id?.first_name + " " + client?.user_id?.last_name;

	const clientData = {
		name: clientName,
		email: client?.user_id?.email,
	};
	const { data: relatives } = useRelatedRecords("relatives", "*.*", "client_id", client?.id);

	const relativesData = relatives?.items?.map((relative: { Nome: string; email: string }) => ({
		name: relative?.Nome || "",
		email: relative?.email || "",
	}));

	const entityForm = useForm({
		defaultValues: {
			content: "",
			attachment: null as unknown as Blob,
			emailData: {
				id: client ? `${client.user_id.email}-${client.user_id.first_name} ${client.user_id.last_name}` : "",
				name: client?.user_id?.first_name || "",
				email: client?.user_id?.email || "",
			},
		},
	});
	const selectedEmailData = entityForm.watch("emailData");
	const selectedEmailName = selectedEmailData?.name?.split(" ")?.[0] || "";

	useEffect(() => {
		entityForm.setValue("content", emailDescription);
	}, [selectedEmailName]);

	useEffect(() => {
		const loadAttachment = async () => {
			try {
				setIsLoading(true);
				const attachmentBlob = await generateTreatmentPrescriptionPdf(context);
				const pdfBlob = new Blob([attachmentBlob.blob], { type: "application/pdf" });
				entityForm.setValue("attachment", pdfBlob);

				const url = URL.createObjectURL(pdfBlob);
				setDownloadUrl(url);
			} catch (error: any) {
				console.error("Error generating PDF:", error);
				setPdfGenerationError(true);
				notifications.setNotification({
					isOpen: true,
					message: error.message || "Erro ao gerar o PDF da prescrição.",
					severity: "error",
				});
			} finally {
				setIsLoading(false);
			}
		};
		loadAttachment();

		return () => {
			if (downloadUrl) URL.revokeObjectURL(downloadUrl);
		};
	}, [context.item.treatment_prescription]);

	const handleSend = async (formData: {
		content: string;
		emailData: { id: string; name: string; email: string };
		attachment: Blob;
	}) => {
		notifications.setNotification({
			isOpen: false,
		});

		setIsLoading(true);
		try {
			const form = new FormData();
			form.append("name", formData.emailData.name);
			form.append("email", formData.emailData.email);
			form.append("subject", "Prescrição de Tratamento - NeuroImprove Clinic");
			form.append("content", formData.content);
			form.append("attachment", formData.attachment, `prescricao_tratamento ${clientName}.pdf`);
			form.append("treatmentId", treatmentId)

			const response = await axios({
				method: "POST",
				url: MAKE_WEBHOOKS.send_email_with_optional_attachment,
				data: form,
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
		const isSuccess = response.status === 200;
		notifications.setNotification({
			isOpen: true,
			message: response.data,
			severity: isSuccess ? "success" : "error",
		});
		const treatmentPrescriptionId = context.item?.treatment_prescription?.[0]?.id;

		if (isSuccess && treatmentPrescriptionId) {
			const currentDate = new Date().toISOString();
			updateRecord({
				entity: "treatment_prescription",
				id: treatmentPrescriptionId,
				data: {
					prescription_sent_date: currentDate,
				},
			});
		}
			entityForm.reset();
			onClose();
		} catch (error: any) {
			notifications.setNotification({
				isOpen: true,
				message: error?.response?.data?.errorMessage || "Erro ao enviar email",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={entityForm.handleSubmit(handleSend)}>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: "600px" }}>
				<PdfDocumentDownloadButton
					downloadUrl={downloadUrl}
					buttonText={`Prescrição de Tratamento - ${clientName}`}
					fileName={`prescricao_tratamento_${clientName}`}
					pdfGenerationError={pdfGenerationError}
					isLoading={isLoading}
				/>
				<Fade in={!!downloadUrl} timeout={500}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{downloadUrl && (
							<>
								<Typography
									variant="body1"
									sx={{ fontWeight: 600, my:4, color: "primary.main" }}
								>
									Enviar Presriçao de Tratamento por email
								</Typography>
								<Box height="270px" overflow="auto" mb={4}>
									<Wysiwyg
										initialValue={emailDescription}
										isDisabled={true}
										canMention={false}
										entityForm={entityForm as unknown as UseFormReturn}
									/>
								</Box>

								<ClientAndRelativesEmailSelectionField
									entityForm={entityForm as unknown as UseFormReturn}
									client={clientData}
									relatives={relativesData}
								/>
							</>
						)}
					</Box>
				</Fade>
			</DialogContent>
			<DialogActions>
				<Button color="secondary" onClick={onClose}>
					Cancelar
				</Button>
				<Button type="submit" disabled={downloadUrl === "" || isLoading}>
					{isLoading && downloadUrl ? <CircularProgress color="inherit" size={24} /> : "Enviar"}
				</Button>
			</DialogActions>
		</form>
	);
};

const emailDescription = `Olá,<br><br>
Segue em anexo a informação referente ao tratamento prescrito.<br>
Se tiver alguma dúvida ou necessitar de esclarecimentos adicionais, estamos inteiramente à disposição.
<br><br>
Obrigado,<br>
NeuroImprove Clinic.`;
